import { Box, Text } from "@mantine/core"
import { CheckIcon } from "@phosphor-icons/react"
import { Fragment } from "react"
import classes from "./order-stepper.module.css"

type Step = { code: string; name: string }

type Props = {
  steps: Step[]
  currentStatus: string
  /** «below» (Ord_SalesDetail): label under the dot. «inline» (Ord_PurchaseDetail): label beside the dot, compact. */
  layout?: "below" | "inline"
  /** Current step: brand filled with a ring (sales) or a dark ring (purchase). */
  currentTone?: "brand" | "dark"
  /** Show a check in done dots instead of a plain filled dot. */
  checkDone?: boolean
  /** The order is past the last step shown: every step is done. */
  allDone?: boolean
}

/** Order progress: done = dark filled dot with dark line, current = ringed numbered dot, pending = grey hollow numbered. */
export function OrderStepper({ steps, currentStatus, layout = "below", currentTone = "brand", checkDone = false, allDone = false }: Props) {
  const currentIndex = allDone ? steps.length : steps.findIndex((s) => s.code === currentStatus)
  const stateOf = (i: number) => (i < currentIndex ? "done" : i === currentIndex ? "current" : "todo")

  const dot = (i: number) => {
    const state = stateOf(i)
    return (
      <Box className={classes.dot} data-state={state} data-tone={currentTone}>
        {state === "done" ? (checkDone ? <CheckIcon size={14} weight="bold" /> : null) : i + 1}
      </Box>
    )
  }

  if (layout === "inline") {
    return (
      <Box className={classes.inline}>
        {steps.map((step, i) => (
          <Fragment key={step.code}>
            {i > 0 && <Box className={classes.line} data-active={i <= currentIndex || undefined} />}
            <Box className={classes.step} aria-current={stateOf(i) === "current" ? "step" : undefined}>
              {dot(i)}
              <Text className={classes.label} data-state={stateOf(i)}>{step.name}</Text>
            </Box>
          </Fragment>
        ))}
      </Box>
    )
  }

  return (
    <Box className={classes.below}>
      {steps.map((step, i) => (
        <Box key={step.code} className={classes.step} data-state={stateOf(i)} aria-current={stateOf(i) === "current" ? "step" : undefined}>
          {dot(i)}
          <Text className={classes.label} data-state={stateOf(i)}>{step.name}</Text>
        </Box>
      ))}
    </Box>
  )
}
